/**
 * TransferVerification.tsx
 * Supervisor page for receiving and verifying incoming raw material transfers.
 * Combined flow: supervisor scans badge, selects a transfer, scans boxes, then
 * makes per-item decisions (approve / adjust / reject) before submitting.
 * Designed for Munbyn IPDA030 (5" touchscreen).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/api';
import {
  inventoryService,
  UnprocessedTransfer,
  TransferItem,
  ReceiveItemDecision,
} from '../../services/inventoryService';
import { parseGS1128Barcode, isBadgeCode, extractEmployeeNumber } from '../../utils/barcodeUtils';
import { useTransferData } from './TransferVerification/useTransferData';
import TransferHistoryTab from './TransferVerification/TransferHistoryTab';
import TransferScanningStep from './TransferVerification/TransferScanningStep';
import TransferDecisionStep from './TransferVerification/TransferDecisionStep';
import TransferSelectionStep from './TransferVerification/TransferSelectionStep';
import toast from 'react-hot-toast';
import {
  CreditCard,
  ArrowLeft,
  History,
  ClipboardCheck,
  User,
  LogOut,
  Sun,
  Moon,
  Globe,
  X,
  LayoutGrid,
  ListChecks,
} from 'lucide-react';

type Step = 'badge' | 'transfer' | 'scanning' | 'decision';
type DecisionMode = 'perItem' | 'wholeTransfer';
const STORAGE_KEY = 'transferVerification';

// Per-item state during the decision step
interface ItemDecision {
  item: TransferItem;
  acceptedCount: number;
  acceptedWeight: number;
  rejectedCount: number;
  rejectedWeight: number;
  onHoldCount?: number; // boxes on hold pending manager approval
  onHoldWeight?: number;
  action?: 'approve' | 'hold' | 'reject';
  onHoldReason?: string;
  notes?: string;
  editing?: boolean;
}

// WCO-2936: rebuild item decisions from the transfer's items + the scanned boxes. This is the single
// source of truth for deriving `itemDecisions` (used by handleProceedToDecision AND by any restore
// path). Because the submit payload's `items` array is built exclusively from `itemDecisions`, a
// checkpoint/localStorage restore that repopulated `scannedBoxes` but NOT `itemDecisions` left the
// summary showing counts (derived from scannedBoxes) while `items` submitted empty → the backend 400
// "transfer_number, verified_by, and items array are required". Deriving decisions from scannedBoxes
// (whose per-box status already carries accept/hold/reject) makes a reload unable to strand the submit.
// The subset of ScannedBox this reconstruction reads (kept minimal so callers/tests can pass partials).
type ScannedBoxLike = Pick<ScannedBox, 'item_number' | 'status' | 'weight' | 'quantity' | 'is_packing_material' | 'notInTransfer'>;

// Count a scanned box: packing materials carry a `quantity` (units per box); everything else is 1 box.
// Guarding on is_packing_material (not just `quantity || 1`) keeps a stray/zero quantity on a
// non-packing box from over- or under-counting (CodeAnt review, PR #724).
const boxCount = (b: ScannedBoxLike): number =>
  b.is_packing_material && b.quantity && b.quantity > 0 ? b.quantity : 1;
export function buildItemDecisions(
  transferItems: TransferItem[],
  scannedBoxes: ScannedBoxLike[],
): ItemDecision[] {
  return transferItems
    .filter(item => !item.already_received)
    .map(item => {
      const acceptedBoxes = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'accepted' && !b.notInTransfer);
      const onHoldBoxes = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'on_hold' && !b.notInTransfer);
      const rejectedBoxes = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'rejected' && !b.notInTransfer);

      const acceptedWeight = acceptedBoxes.reduce((sum, b) => sum + (b.weight || 0), 0);
      const onHoldWeight = onHoldBoxes.reduce((sum, b) => sum + (b.weight || 0), 0);
      const rejectedWeight = rejectedBoxes.reduce((sum, b) => sum + (b.weight || 0), 0);

      // Packing materials use the quantity field instead of box count; everything else counts as 1.
      const acceptedCount = acceptedBoxes.reduce((sum, b) => sum + boxCount(b), 0);
      const onHoldCount = onHoldBoxes.reduce((sum, b) => sum + boxCount(b), 0);
      const rejectedCount = rejectedBoxes.reduce((sum, b) => sum + boxCount(b), 0);

      return {
        item,
        acceptedCount,
        acceptedWeight,
        rejectedCount,
        rejectedWeight,
        onHoldCount,
        onHoldWeight,
      };
    });
}

// Scanner threshold
const SCANNER_KEYSTROKE_THRESHOLD = 400;
const MIN_BARCODE_LENGTH = 5;

type ScannedBox = {
  gtin: string;
  weight: number;
  raw_weight?: number;
  weight_ai?: string;
  weight_unit?: 'kg' | 'lb';
  quantity?: number;
  is_packing_material?: boolean;
  item_number?: string;
  item_name?: string;
  status: 'accepted' | 'rejected' | 'on_hold';
  notInTransfer?: boolean;
  rejectReason?: string;
  onHoldReason?: string;
  manualReason?: string;
};

type PendingBox = {
  gtin: string;
  weight: number;
  raw_weight?: number;
  weight_ai?: string;
  weight_unit?: 'kg' | 'lb';
  quantity?: number;
  is_packing_material?: boolean;
  item_number: string;
  item_name?: string;
  notInTransfer: boolean;
};

// WCO-3121/3144: may this pending box's weight be committed to scannedBoxes?
// A non-packing box must have a finite weight > 0 — a checkpoint/localStorage restore could seed a
// pendingBox that never passed through processBoxScan's scan-boundary weight check, so we re-guard
// here. Packing material is counted by quantity and legitimately carries weight 0 (the manual-add
// path sets `weight: is_packing_material ? 0 : value`), so 0 is allowed only for `is_packing_material`.
// NaN / null / negative are always rejected. Exported so the guard is unit-testable without the full UI.
export function isCommittableBoxWeight(box: Pick<PendingBox, 'weight' | 'is_packing_material'>): boolean {
  const w = box.weight as number;
  if (!Number.isFinite(w) || w < 0) return false;
  if (w === 0 && !box.is_packing_material) return false;
  return true;
}

// WCO-3144: say WHY a box failed the weight gate, in the supervisor's terms, keeping the raw value
// visible for diagnostics. The gate rejects three things on a raw-material box — 0, negative, and a
// non-finite/absent weight — and the message must name the one that actually happened rather than
// describe where the box came from. NB NaN/Infinity cannot survive JSON serialization (localStorage
// string and jsonb checkpoint alike persist them as null), so through the restore path — the only
// path that reaches this gate — the realistic cases are 0, negative and null/undefined.
// (Packing material legitimately carries 0 and never reaches here; see isCommittableBoxWeight.)
export function describeBadWeight(weight: unknown, t: (key: string) => string): string {
  if (weight === null || weight === undefined) return t('transferVerification.weightMissing');
  if (typeof weight !== 'number' || !Number.isFinite(weight)) return t('transferVerification.weightUnreadable');
  return `${weight.toFixed(2)} lbs`;
}

// WCO-3144: the submit-time weight gate. Returns the FIRST scanned box that both COUNTS toward
// received weight and has an uncommittable weight — i.e. must block submission. Only accepted /
// on_hold, in-transfer boxes are gated: a `rejected` box is being discarded (its weight is
// irrelevant) and a `notInTransfer` box never enters the received totals. This is the single
// chokepoint over every entry into scannedBoxes (live scan AND localStorage / checkpoint restore),
// so it can't be bypassed by a future new entry path the way handleBoxDecision's fresh-scan-only
// guard can. Exported so the selection logic is unit-testable without rendering the component.
export function findUncommittableScannedBox<
  T extends Pick<ScannedBox, 'weight' | 'is_packing_material' | 'status' | 'notInTransfer'>,
>(boxes: T[]): T | undefined {
  return boxes.find(
    b => !b.notInTransfer && b.status !== 'rejected' && !isCommittableBoxWeight(b),
  );
}

interface TransferVerificationProps {
  isMobile?: boolean;
  /** When true, component is rendered inside a sidebar panel — hides its own header and uses compact styling */
  embedded?: boolean;
  initialTransferNumber?: string;
  onBack?: () => void;
  onSuccess?: () => void;
}

const TransferVerification: React.FC<TransferVerificationProps> = ({ isMobile = false, embedded = false, initialTransferNumber, onBack, onSuccess }) => {
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  // dateInputRef removed — DatePickerDropdown handles its own state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Restore persisted session (survives page refresh)
  // Only restore scannedBoxes if the saved transfer matches the current one
  const saved = useRef(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // If we're opening a specific transfer (via sidebar), only restore if it matches
      if (initialTransferNumber && parsed.transferNumber && parsed.transferNumber !== initialTransferNumber) {
        // Different transfer — keep supervisor info but discard scan progress
        return { ...parsed, scannedBoxes: [], selectedTransfer: null, step: parsed.supervisorId ? 'transfer' : 'badge' };
      }
      return parsed;
    } catch { return null; }
  }).current();

  // Step tracking
  const [step, setStep] = useState<Step>(saved?.step || 'badge');

  // Data loading (transfers, history, week navigation, tab mode)
  const {
    transfers, loadingTransfers, weekOffset, setWeekOffset, rangeDays, setRangeDays, loadTransfers,
    historyTransfers, loadingHistory, historyPage, setHistoryPage, historyTotalPages,
    expandedHistoryTransfer, setExpandedHistoryTransfer, expandedHistoryBoxes, setExpandedHistoryBoxes,
    loadHistory, tabMode, setTabMode,
  } = useTransferData({ step });

  // Badge / box scan input — uncontrolled (ClockInModal pattern). The DOM value is the single source
  // of truth: HID keyboard-wedge and manual/IME typing both populate it natively. Enter reads
  // e.currentTarget.value (always complete, no WCO-3121 stale-closure race). Do NOT add a controlled
  // value + onChange setter alongside handleKeyDown char accumulation — that double-writes scans.
  const lastKeystrokeTime = useRef<number>(0);
  const scanTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearScanInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };
  const [isVerifying, setIsVerifying] = useState(false);
  const [supervisorId, setSupervisorId] = useState(saved?.supervisorId || '');
  const [supervisorName, setSupervisorName] = useState(saved?.supervisorName || '');

  // Transfer selection state
  const [selectedTransfer, setSelectedTransfer] = useState<UnprocessedTransfer | null>(saved?.selectedTransfer || null);
  const [transferScanBuffer, setTransferScanBuffer] = useState('');
  const [transferScanError, setTransferScanError] = useState('');
  const [isLookingUpTransfer, setIsLookingUpTransfer] = useState(false);
  const transferInputRef = useRef<HTMLInputElement>(null);

  // Box scanning state
  const [scannedBoxes, setScannedBoxes] = useState<ScannedBox[]>(saved?.scannedBoxes || []);
  const [scanError, setScanError] = useState('');
  const [pendingBox, setPendingBox] = useState<PendingBox | null>(null);
  const [showRejectReasons, setShowRejectReasons] = useState(false);
  const [showHoldReasons, setShowHoldReasons] = useState(false);

  // Manual add state (for damaged labels that can't be scanned)
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualSelectedItem, setManualSelectedItem] = useState<string | null>(null);
  const [manualWeight, setManualWeight] = useState('');
  const [manualReason, setManualReason] = useState('');
  const pendingManualReason = useRef<string>('');
  const manualWeightRef = useRef<HTMLInputElement>(null);

  // Scan summary modal state
  const [showScanSummary, setShowScanSummary] = useState(false);

  // Decision state
  // WCO-2936 (Ola/Cesar review): seed itemDecisions from the persisted session on mount. A browser
  // refresh rehydrates selectedTransfer + scannedBoxes straight into state without ever calling
  // handleSelectTransfer/handleProceedToDecision (the writers of itemDecisions), so without this the
  // decision screen (TransferDecisionStep renders from itemDecisions) would show a blank Review Items
  // list even though the box counts look complete. Deriving here mirrors the checkpoint-restore path.
  const [itemDecisions, setItemDecisions] = useState<ItemDecision[]>(() =>
    saved?.selectedTransfer && saved?.scannedBoxes?.length
      ? buildItemDecisions(saved.selectedTransfer.items, saved.scannedBoxes)
      : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [decisionMode, setDecisionMode] = useState<DecisionMode>('perItem');
  const [wholeTransferAction, setWholeTransferAction] = useState<'approve' | 'reject' | null>(null);
  const [wholeTransferNotes, setWholeTransferNotes] = useState('');

  // Error state
  const [error, setError] = useState('');

  // Focus input when step changes
  useEffect(() => {
    if (step === 'badge' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else if (step === 'transfer' && transferInputRef.current) {
      setTimeout(() => transferInputRef.current?.focus(), 100);
    } else if (step === 'scanning' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [step]);

  // Clean up on unmount — clear persisted session so badge resets on re-entry
  useEffect(() => {
    return () => {
      if (scanTimeout.current) clearTimeout(scanTimeout.current);
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  // Close mobile user menu on click outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Persist session state so a page refresh resumes where we left off
  // Includes transfer_number so we only restore data for the correct transfer
  useEffect(() => {
    if (step === 'badge') return; // nothing worth saving yet
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      step, supervisorId, supervisorName, selectedTransfer, scannedBoxes,
      transferNumber: selectedTransfer?.transfer_number || null,
    }));
  }, [step, supervisorId, supervisorName, selectedTransfer, scannedBoxes]);

  // Backend checkpoint save — fires immediately on every scan/decision change
  // Box decisions are infrequent (one every few seconds), so no debounce needed
  const lastSavedRef = useRef<string>('');
  useEffect(() => {
    if (step === 'badge' || !selectedTransfer || scannedBoxes.length === 0) return;
    // Deduplicate: only save if data actually changed
    const key = `${selectedTransfer.transfer_number}:${scannedBoxes.length}:${step}:${decisionMode}`;
    if (key === lastSavedRef.current) return;
    lastSavedRef.current = key;

    inventoryService.saveReceivingCheckpoint(selectedTransfer.transfer_number, {
      supervisorId, supervisorName, step, scannedBoxes, decisionMode,
    }).catch(() => {
      toast.error(t('transferVerification.checkpointSaveError'));
    });
  }, [scannedBoxes, step, decisionMode]);

  // Auto-lookup transfer when initialTransferNumber is provided (from WarehouseTransferManagement)
  const autoLookupDone = useRef(false);
  useEffect(() => {
    if (initialTransferNumber && step === 'transfer' && !autoLookupDone.current) {
      autoLookupDone.current = true;
      handleTransferScan(initialTransferNumber);
    }
  }, [step, initialTransferNumber]);

  // ===== Badge scan handling =====
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (scanTimeout.current) clearTimeout(scanTimeout.current);

    if (e.key === 'Enter') {
      e.preventDefault();
      // DOM value is authoritative — populated by HID wedge or manual/IME typing, never double-written.
      const buffered = e.currentTarget.value;
      if (step === 'badge' && buffered.length >= 3) {
        await verifyBadge(buffered);
      } else if (step === 'scanning' && buffered.length >= MIN_BARCODE_LENGTH) {
        await processBoxScan(buffered);
      }
      clearScanInput();
      lastKeystrokeTime.current = 0;
      return;
    }

    if (e.key.length !== 1) return;

    const isFirstKey = lastKeystrokeTime.current === 0;
    const isScannerSpeed = (now - lastKeystrokeTime.current) < SCANNER_KEYSTROKE_THRESHOLD;

    lastKeystrokeTime.current = now;

    // Auto-clear abandoned fast scanner bursts; slow manual typing is left alone.
    if (isFirstKey || isScannerSpeed) {
      scanTimeout.current = setTimeout(() => {
        clearScanInput();
        lastKeystrokeTime.current = 0;
      }, 2000);
    }
  };

  const verifyBadge = async (badge: string) => {
    setIsVerifying(true);
    setError('');
    try {
      const employeeNumber = extractEmployeeNumber(badge);
      const result = await authService.verifySupervisorBadge(employeeNumber);
      if (result.valid) {
        // Use employeeNumber for verified_by so it can be joined with ics.employee for name lookup
        setSupervisorId(result.employeeNumber || result.userId!);
        setSupervisorName(result.fullName!);
        toast.success(t('operator.supervisorVerified'));
        setStep('transfer');
      } else {
        setError(result.error || t('operator.invalidSupervisorBadge'));
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch {
      setError(t('operator.invalidSupervisorBadge'));
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      setIsVerifying(false);
    }
  };

  // ===== Transfer selection =====
  // Loads checkpoint from backend if one exists (parent already showed resume prompt for sidebar switches)
  const handleSelectTransfer = async (transfer: UnprocessedTransfer) => {
    setScanError('');
    setTransferScanBuffer('');
    setTransferScanError('');

    // Try to restore from backend checkpoint
    try {
      const checkpoint = await inventoryService.getReceivingCheckpoint(transfer.transfer_number);
      if (checkpoint && checkpoint.scannedBoxes && checkpoint.scannedBoxes.length > 0) {
        setSelectedTransfer(transfer);
        setScannedBoxes(checkpoint.scannedBoxes);
        if (checkpoint.decisionMode) setDecisionMode(checkpoint.decisionMode as DecisionMode);
        // WCO-2936: the checkpoint restores scannedBoxes but NOT itemDecisions — and the submit's
        // `items` array is built from itemDecisions. Without this, a restore to the decision step
        // left itemDecisions empty → submit sent items:[] → backend 400. Rebuild them from the
        // restored boxes + the transfer's items so the submit is never stranded.
        setItemDecisions(buildItemDecisions(transfer.items, checkpoint.scannedBoxes));
        setStep(checkpoint.step === 'decision' ? 'decision' : 'scanning');
        return;
      }
    } catch { /* proceed without checkpoint */ }

    setSelectedTransfer(transfer);
    setScannedBoxes([]);
    setStep('scanning');
  };

  // Handle transfer barcode scan
  const handleTransferScan = async (transferNumber: string) => {
    setTransferScanError('');
    setIsLookingUpTransfer(true);
    try {
      const transfer = await inventoryService.getTransferByNumber(transferNumber.trim());
      if (transfer) {
        // Check if transfer is already fully verified
        if (transfer.received_items >= transfer.total_items && transfer.total_items > 0) {
          setTransferScanError(t('transferVerification.alreadyVerified'));
          setTimeout(() => transferInputRef.current?.focus(), 100);
        } else {
          handleSelectTransfer(transfer);
        }
      } else {
        setTransferScanError(t('transferVerification.transferNotFound'));
        setTimeout(() => transferInputRef.current?.focus(), 100);
      }
    } catch (err) {
      setTransferScanError(t('transferVerification.transferLookupError'));
      setTimeout(() => transferInputRef.current?.focus(), 100);
    } finally {
      setIsLookingUpTransfer(false);
      setTransferScanBuffer('');
    }
  };

  // Handle keydown for transfer scan input
  const handleTransferKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (transferScanBuffer.length >= 3) {
        handleTransferScan(transferScanBuffer);
      }
    }
  };

  // ===== Box scanning =====
  const processBoxScan = async (barcode: string) => {
    setScanError('');

    // Don't allow new scan while pending box awaits decision (only in perItem mode)
    if (decisionMode === 'perItem' && pendingBox) {
      setScanError(t('transferVerification.decidePendingFirst'));
      return;
    }

    // Ignore badge scans during box scanning
    if (isBadgeCode(barcode)) {
      setScanError(t('transferVerification.badgeNotBox'));
      return;
    }

    // Try to parse as GS1-128
    const parsed = parseGS1128Barcode(barcode);
    if (!parsed || !parsed.gtin) {
      setScanError(t('transferVerification.invalidBarcode'));
      return;
    }

    if (!selectedTransfer) {
      setScanError(t('transferVerification.noTransferSelected'));
      return;
    }

    // Get pending items for this transfer
    const pendingItems = selectedTransfer.items.filter(i => !i.already_received);
    const pendingItemNumbers = new Set(pendingItems.map(i => i.item_number));

    // Look up item_number from GTIN via item_to_upc table
    let matchedItemNumber: string | undefined;
    let matchedItemName: string | undefined;
    let notInTransfer = false;

    try {
      const gtinResult = await inventoryService.lookupGtin(parsed.gtin);
      if (gtinResult) {
        matchedItemNumber = gtinResult.item_number;
        // WCO-3121: catalog unit_weight is no longer used as a weight fallback — a catch-weight box
        // must carry its own weight in the barcode, and a scan without a valid weight is rejected below.
        // Check if this item is in the transfer
        if (pendingItemNumbers.has(gtinResult.item_number)) {
          // Item is in transfer - get name from transfer items
          const transferItem = pendingItems.find(i => i.item_number === gtinResult.item_number);
          matchedItemName = transferItem?.item_name;
        } else {
          // Item exists but NOT in this transfer
          notInTransfer = true;
          // We have the item_number, name will be shown as item_number
        }
      }
    } catch (err) {
      console.error('[TransferVerification] GTIN lookup failed:', err);
    }

    // If GTIN not found at all in the system
    if (!matchedItemNumber) {
      setScanError(t('transferVerification.unknownBarcode'));
      return;
    }

    // WCO-3121: the barcode's own weight is the source of truth for a catch-weight case. Resolve it
    // WITHOUT a silent catalog fallback, then reject any scan whose weight isn't a finite number > 0.
    // `??` does NOT catch NaN (NaN ?? x === NaN), so a truncated/dirty read (parseInt('') → NaN) or a
    // 0 lb / undefined result would previously slip through as a bad box. Reject at the boundary so no
    // NaN/0 box is ever created — before the pending box AND before any wholeTransfer auto-accept.
    const normalizedWeight = parsed.normalizedWeightLb;
    const rawBarcodeWeight = parsed.weight;

    if (!Number.isFinite(normalizedWeight) || (normalizedWeight as number) <= 0) {
      // Capture the raw scanned string so the underlying field cause (truncation/scanner config/label
      // variant) can be diagnosed later — it isn't captured anywhere else (WCO-3121 Defect A). Local
      // console for the dev looking now + a DURABLE backend diagnostic (handheld console is transient).
      console.warn('[TransferVerification] Rejected scan — unreadable/invalid weight', {
        rawBarcode: barcode,
        parsedWeight: parsed.weight,
        parsedNormalizedLb: parsed.normalizedWeightLb,
        weightAI: parsed.weightAI,
        weightUnit: parsed.weightUnit,
        gtin: parsed.gtin,
      });
      inventoryService.logScanDiagnostic({
        raw_barcode: barcode,
        reason: 'bad_weight',
        transfer_number: selectedTransfer?.transfer_number,
        verified_by: supervisorId || undefined,
        parsed_weight: parsed.weight,
        parsed_normalized_lb: parsed.normalizedWeightLb,
        weight_ai: parsed.weightAI,
        weight_unit: parsed.weightUnit,
        gtin: parsed.gtin,
      });
      setScanError(t('transferVerification.badWeight'));
      // Keep focus so the supervisor can immediately re-scan or hit Manual Add.
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    // Past the guard, normalizedWeight is a finite number > 0.
    const validWeight: number = normalizedWeight as number;

    // In Whole Transfer mode: auto-accept boxes (no per-box decision)
    if (decisionMode === 'wholeTransfer') {
      setScannedBoxes(prev => [...prev, {
        gtin: parsed.gtin || '',
        weight: validWeight,  // Use normalized lb value for all downstream totals
        raw_weight: rawBarcodeWeight,
        weight_ai: parsed.weightAI,
        weight_unit: parsed.weightUnit,
        item_number: matchedItemNumber,
        item_name: matchedItemName,
        status: 'accepted',
        notInTransfer,
      }]);
      // Keep focus on input for next scan
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    // In Per Item mode: Set as pending box - user must accept or reject
    setPendingBox({
      gtin: parsed.gtin,
      weight: validWeight,  // Use normalized lb value for all downstream totals
      raw_weight: rawBarcodeWeight,
      weight_ai: parsed.weightAI,
      weight_unit: parsed.weightUnit,
      item_number: matchedItemNumber,
      item_name: matchedItemName,
      notInTransfer,
    });
  };

  // Accept or reject the pending box
  const handleBoxDecision = (status: 'accepted' | 'rejected' | 'on_hold', reason?: string) => {
    if (!pendingBox) return;

    // WCO-3121 defence in depth: never commit a bad weight to scannedBoxes via a FRESH scan. The
    // scan/manual paths already guard this; this re-check catches a pendingBox whose weight slipped
    // through (e.g. a manual-add edge case) before it lands in scannedBoxes. NB: pendingBox is never
    // restored (it's useState(null) — only scannedBoxes is restored), so the checkpoint/localStorage
    // restore path is NOT covered here — that is handled by the submit-time gate in handleSubmit
    // (WCO-3144), the single chokepoint over every entry into scannedBoxes.
    if (!isCommittableBoxWeight(pendingBox)) {
      setScanError(t('transferVerification.badWeight'));
      setPendingBox(null);
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }

    const manualReasonValue = pendingBox.gtin === 'MANUAL' ? pendingManualReason.current : undefined;
    setScannedBoxes(prev => [...prev, {
      gtin: pendingBox.gtin,
      weight: pendingBox.weight,
      raw_weight: pendingBox.raw_weight,
      weight_ai: pendingBox.weight_ai,
      weight_unit: pendingBox.weight_unit,
      quantity: pendingBox.quantity,
      is_packing_material: pendingBox.is_packing_material,
      item_number: pendingBox.item_number,
      item_name: pendingBox.item_name,
      status,
      notInTransfer: pendingBox.notInTransfer,
      rejectReason: status === 'rejected' ? reason : undefined,
      onHoldReason: status === 'on_hold' ? reason : undefined,
      manualReason: manualReasonValue,
    }]);
    setPendingBox(null);
    pendingManualReason.current = '';
    setShowRejectReasons(false);
    setShowHoldReasons(false);

    // Re-focus input for next scan
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const removeScannedBox = (index: number) => {
    setScannedBoxes(prev => prev.filter((_, i) => i !== index));
  };

  // Handle manual add confirmation — creates a box entry from manual item selection + weight/qty + reason
  const handleManualAddConfirm = (reason: string) => {
    if (!manualSelectedItem || !manualWeight || !selectedTransfer || !reason) return;
    const value = parseFloat(manualWeight);
    if (isNaN(value) || value <= 0) return;

    const pendingItems = selectedTransfer.items.filter(i => !i.already_received);
    const matchedItem = pendingItems.find(i => i.item_number === manualSelectedItem);
    const isPacking = matchedItem?.is_packing_material === 'Y';

    if (decisionMode === 'wholeTransfer') {
      setScannedBoxes(prev => [...prev, {
        gtin: 'MANUAL',
        weight: isPacking ? 0 : value,
        quantity: isPacking ? Math.round(value) : undefined,
        is_packing_material: isPacking,
        item_number: manualSelectedItem,
        item_name: matchedItem?.item_name,
        status: 'accepted',
        notInTransfer: !matchedItem,
        manualReason: reason,
      }]);
    } else {
      pendingManualReason.current = reason;
      setPendingBox({
        gtin: 'MANUAL',
        weight: isPacking ? 0 : value,
        quantity: isPacking ? Math.round(value) : undefined,
        is_packing_material: isPacking,
        item_number: manualSelectedItem,
        item_name: matchedItem?.item_name,
        notInTransfer: !matchedItem,
      });
    }

    // Reset manual add form
    setShowManualAdd(false);
    setManualSelectedItem(null);
    setManualWeight('');
    setManualReason('');
  };

  // ===== Move to decision step =====
  const handleProceedToDecision = () => {
    if (!selectedTransfer) return;
    // WCO-2936: derive via the shared helper so this and the restore path stay in lockstep.
    setItemDecisions(buildItemDecisions(selectedTransfer.items, scannedBoxes));
    setStep('decision');
  };

  // In per-item mode, decisions are complete if we have scanned boxes
  const allDecisionsComplete = scannedBoxes.length > 0;
  const wholeTransferComplete = scannedBoxes.length > 0;

  // ===== Submit =====
  const handleSubmit = async () => {
    if (!selectedTransfer) return;

    // Validate based on decision mode
    if (decisionMode === 'perItem' && !allDecisionsComplete) return;
    if (decisionMode === 'wholeTransfer' && !wholeTransferComplete) return;

    // WCO-3144: submit-time weight gate — the single chokepoint that covers ALL entry paths into
    // scannedBoxes (live scan AND localStorage/backend-checkpoint restore). handleBoxDecision only
    // guards a freshly-scanned pendingBox; a legacy 0-lb / NaN / null non-packing box that was
    // persisted before that guard existed is restored straight into scannedBoxes and would otherwise
    // reach the backend unvalidated (under-booking received weight — the WCO-2938 class). Surface the
    // offending box (never silently drop it) so the supervisor re-scans or rejects it.
    const invalidBox = findUncommittableScannedBox(scannedBoxes);
    if (invalidBox) {
      const label = invalidBox.item_name || invalidBox.item_number || invalidBox.gtin;
      // Point at ONE row: the item name repeats across every box of the same item, so the name alone
      // doesn't say which one is bad. This is the same 1-based scan order the Scanned Items list
      // renders and that buildScanDetails sends as scan_order.
      const boxNumber = scannedBoxes.indexOf(invalidBox) + 1;
      toast.error(
        `${t('transferVerification.invalidBoxWeight')} #${boxNumber} ${label} — ${describeBadWeight(invalidBox.weight, t)}`
      );
      return;
    }

    setSubmitting(true);

    try {
      let items: ReceiveItemDecision[];

      // WCO-2936 safety net: `items` is built from itemDecisions. If a checkpoint/localStorage restore
      // repopulated scannedBoxes but left itemDecisions empty (any restore path we don't hook), rebuild
      // them here from the scanned boxes so the submit never sends items:[] (backend 400). setState is
      // async and won't reflect this render, so compute a local and use it for the build below.
      const effectiveDecisions = (itemDecisions.length === 0 && scannedBoxes.length > 0)
        ? buildItemDecisions(selectedTransfer.items, scannedBoxes)
        : itemDecisions;
      if (effectiveDecisions !== itemDecisions) setItemDecisions(effectiveDecisions);

      // Helper: build remark from box's reject reason, hold reason and/or manual reason
      const buildRemark = (box: typeof scannedBoxes[0]) => {
        const parts: string[] = [];
        if (box.rejectReason) parts.push(box.rejectReason);
        if (box.onHoldReason) parts.push(`Hold: ${box.onHoldReason}`);
        if (box.manualReason) parts.push(`Manual: ${box.manualReason}`);
        return parts.length > 0 ? parts.join(' · ') : undefined;
      };

      // Helper: build scan_details for boxes matching a given item + status filter
      const buildScanDetails = (boxes: typeof scannedBoxes) =>
        boxes.map((box, _) => {
          const scanIdx = scannedBoxes.indexOf(box);
          return {
            scan_order: scanIdx + 1,
            weight: box.weight,
            raw_weight: box.raw_weight,
            gtin: box.gtin === 'MANUAL' ? null : box.gtin,
            status: box.status,
            remark: buildRemark(box),
            weight_ai: box.weight_ai || null,
            weight_unit: box.weight_unit || null,
          };
        });

      if (decisionMode === 'wholeTransfer') {
        // Auto-approve all items with scanned quantities
        items = effectiveDecisions.map(d => {
          const itemBoxes = scannedBoxes.filter(b => b.item_number === d.item.item_number && !b.notInTransfer);
          return {
            item_number: d.item.item_number,
            action: 'approve' as const,
            expected_quantity: d.item.expected_quantity,
            expected_weight: d.item.expected_weight,
            actual_quantity: d.acceptedCount,
            actual_weight: d.acceptedWeight,
            scan_details: buildScanDetails(itemBoxes),
          };
        });
      } else {
        // Per-item mode: create separate entries for accepted, on-hold, and rejected
        items = [];
        for (const d of effectiveDecisions) {
          const acceptedBoxes = scannedBoxes.filter(b => b.item_number === d.item.item_number && b.status === 'accepted' && !b.notInTransfer);
          const onHoldBoxes = scannedBoxes.filter(b => b.item_number === d.item.item_number && b.status === 'on_hold' && !b.notInTransfer);
          const rejectedBoxes = scannedBoxes.filter(b => b.item_number === d.item.item_number && b.status === 'rejected' && !b.notInTransfer);

          // Add approved lot if there are accepted boxes
          const acceptedWeight = acceptedBoxes.reduce((sum, b) => sum + (b.weight || 0), 0);
          const acceptedQty = acceptedBoxes.reduce((sum, b) => sum + boxCount(b), 0);
          if (acceptedBoxes.length > 0) {
            items.push({
              item_number: d.item.item_number,
              action: 'approve' as const,
              expected_quantity: d.item.expected_quantity,
              expected_weight: d.item.expected_weight,
              actual_quantity: acceptedQty,
              actual_weight: acceptedWeight,
              scan_details: buildScanDetails(acceptedBoxes),
            });
          }

          // Add on-hold lot if there are held boxes (pending manager approval)
          if (onHoldBoxes.length > 0) {
            // Get unique hold reasons from boxes
            const holdReasons = onHoldBoxes.map(b => b.onHoldReason).filter(Boolean);
            const uniqueReasons = [...new Set(holdReasons)];
            const primaryReason = uniqueReasons[0] || 'other';
            const onHoldWeight = onHoldBoxes.reduce((sum, b) => sum + (b.weight || 0), 0);
            const onHoldQty = onHoldBoxes.reduce((sum, b) => sum + boxCount(b), 0);

            items.push({
              item_number: d.item.item_number,
              action: 'hold' as const,
              expected_quantity: d.item.expected_quantity,
              expected_weight: d.item.expected_weight,
              actual_quantity: onHoldQty,
              actual_weight: onHoldWeight,
              notes: `${onHoldBoxes.length} ${onHoldBoxes.length === 1 ? 'box' : 'boxes'} on hold`,
              on_hold_reason: primaryReason,
              scan_details: buildScanDetails(onHoldBoxes),
            });
          }

          // Add rejected lot if there are rejected boxes
          if (d.rejectedCount > 0) {
            const reasons = rejectedBoxes.map(b => b.rejectReason).filter(Boolean);
            const uniqueReasons = [...new Set(reasons)];

            items.push({
              item_number: d.item.item_number,
              action: 'reject' as const,
              expected_quantity: d.item.expected_quantity,
              expected_weight: d.item.expected_weight,
              actual_quantity: d.rejectedCount,
              actual_weight: d.rejectedWeight,
              notes: uniqueReasons.length > 0 ? uniqueReasons.join(', ') : `${d.rejectedCount} ${d.rejectedCount === 1 ? 'box' : 'boxes'} rejected`,
              scan_details: buildScanDetails(rejectedBoxes),
            });
          }
        }

        // Add rejected "not in transfer" items
        const notInTransferRejected = scannedBoxes.filter(b => b.notInTransfer && b.status === 'rejected');
        // Group by item_number
        const notInTransferByItem = notInTransferRejected.reduce((acc, box) => {
          const key = box.item_number || 'unknown';
          if (!acc[key]) {
            acc[key] = { count: 0, weight: 0, reasons: [] as string[], boxes: [] as typeof scannedBoxes };
          }
          acc[key].count += 1;
          acc[key].weight += box.weight || 0;
          if (box.rejectReason) {
            acc[key].reasons.push(box.rejectReason);
          }
          acc[key].boxes.push(box);
          return acc;
        }, {} as Record<string, { count: number; weight: number; reasons: string[]; boxes: typeof scannedBoxes }>);

        for (const [itemNumber, data] of Object.entries(notInTransferByItem)) {
          const uniqueReasons = [...new Set(data.reasons)];
          items.push({
            item_number: itemNumber,
            action: 'reject' as const,
            expected_quantity: 0,
            expected_weight: 0,
            actual_quantity: data.count,
            actual_weight: data.weight,
            notes: `Not in transfer. ${uniqueReasons.length > 0 ? uniqueReasons.join(', ') : 'Rejected'}`,
            scan_details: buildScanDetails(data.boxes),
          });
        }
      }

      await inventoryService.receiveAndVerify({
        transfer_number: selectedTransfer.transfer_number,
        verified_by: supervisorId,
        items,
      });

      const approved = items.filter(i => i.action === 'approve').length;
      const onHold = items.filter(i => i.action === 'hold').length;
      const rejected = items.filter(i => i.action === 'reject').length;

      toast.success(
        `${t('transferVerification.verificationComplete')}: ${approved} ${t('transferVerification.approved')}, ${onHold} ${t('transferVerification.hold')}, ${rejected} ${t('transferVerification.rejected')}`
      );

      // Clear persisted session on successful submission
      localStorage.removeItem(STORAGE_KEY);

      // If parent provided onSuccess callback (URL-based routing), navigate away
      if (onSuccess) {
        setShowConfirmDialog(false);
        onSuccess();
        return;
      }

      // Reset to transfer selection (standalone mobile route)
      setShowConfirmDialog(false);
      setSelectedTransfer(null);
      setScannedBoxes([]);
      setItemDecisions([]);
      setWholeTransferAction(null);
      setWholeTransferNotes('');
      setStep('transfer');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('transferVerification.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Render helpers =====

  const renderBadgeStep = () => (
    <div className={`flex flex-col items-center justify-center p-4 ${embedded ? 'min-h-[30vh]' : 'min-h-[60vh]'}`}>
      <div className={`w-full ${embedded ? 'max-w-full' : 'max-w-md'}`}>
        <div className={`flex items-center gap-3 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg ${embedded ? 'p-3 mb-4' : ''}`}>
          <CreditCard className={`${embedded ? 'w-6 h-6' : 'w-8 h-8'} text-blue-600 dark:text-blue-400 flex-shrink-0`} />
          <div>
            <h2 className={`${embedded ? 'text-sm' : 'text-lg'} font-semibold text-blue-800 dark:text-blue-200`}>
              {t('transferVerification.scanBadge')}
            </h2>
            <p className={`${embedded ? 'text-xs' : 'text-sm'} text-blue-600 dark:text-blue-400`}>
              {t('transferVerification.scanBadgeSubtitle')}
            </p>
          </div>
        </div>

        <input
          ref={inputRef}
          type="text"
          inputMode="none"
          onTouchStart={(e) => { e.currentTarget.inputMode = 'text'; }}
          onKeyDown={handleKeyDown}
          onPaste={(e) => e.preventDefault()}
          placeholder={t('operator.waitingForBadgeScan')}
          className={`w-full border-2 border-blue-300 dark:border-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center ${embedded ? 'px-3 py-3 text-sm' : 'px-4 py-4 text-lg'}`}
          autoComplete="off"
          disabled={isVerifying}
        />

        {isVerifying && (
          <p className="mt-3 text-center text-gray-500 dark:text-gray-400">
            {t('operator.verifyingCredentials')}
          </p>
        )}

        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    </div>
  );



  // Main content that's shared between mobile and desktop
  const mainContent = (
    <>
      {/* Tab switcher */}
      <div className={`flex gap-2 mt-4 px-4 items-center justify-center ${embedded ? 'mt-2 px-3 gap-1' : ''}`}>
        {/* Mobile-only back arrow — on badge step: go to dashboard; otherwise: reset to badge */}
        {!embedded && (
          <button
            onClick={() => {
              if (step === 'badge') {
                navigate('/supervisor');
              } else {
                setSupervisorId('');
                setSupervisorName('');
                setSelectedTransfer(null);
                setScannedBoxes([]);
                setStep('badge');
                clearScanInput();
              }
            }}
            className="md:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={() => setTabMode('verify')}
          className={`flex-1 flex items-center justify-center gap-2 ${embedded ? 'py-1.5 px-2 text-sm' : 'py-2 px-4'} rounded-lg font-medium transition-colors ${tabMode === 'verify'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
        >
          <ClipboardCheck className={embedded ? 'w-4 h-4' : 'w-4 h-4'} />
          {t('transferVerification.verifyTab')}
        </button>
        <button
          onClick={() => setTabMode('history')}
          className={`flex-1 flex items-center justify-center gap-2 ${embedded ? 'py-1.5 px-2 text-sm' : 'py-2 px-4'} rounded-lg font-medium transition-colors ${tabMode === 'history'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
        >
          <History className={embedded ? 'w-4 h-4' : 'w-4 h-4'} />
          {t('transferVerification.historyTab')}
        </button>
        {/* Invisible spacer to balance the back arrow so tabs stay centered */}
        {!embedded && <div className="md:hidden w-9 shrink-0" />}
      </div>

      {/* Step indicator - only show in verify mode */}
      {tabMode === 'verify' && (
        <div className={`flex gap-2 mt-3 px-4 ${embedded ? 'mt-2 px-3 gap-1' : ''}`}>
          {(['badge', 'transfer', 'scanning', 'decision'] as Step[]).map((s, i) => (
            <div
              key={s}
              className={`flex-1 h-1.5 rounded-full ${i <= ['badge', 'transfer', 'scanning', 'decision'].indexOf(step)
                  ? 'bg-primary-600'
                  : 'bg-gray-200 dark:bg-gray-700'
                }`}
            />
          ))}
        </div>
      )}

      {/* Tab content */}
      {tabMode === 'verify' && (
        <>
          {step === 'badge' && renderBadgeStep()}
          {step === 'transfer' && (
            <TransferSelectionStep
              supervisorName={supervisorName}
              transfers={transfers}
              loadingTransfers={loadingTransfers}
              loadTransfers={loadTransfers}
              weekOffset={weekOffset}
              setWeekOffset={setWeekOffset}
              rangeDays={rangeDays}
              setRangeDays={setRangeDays}
              transferScanBuffer={transferScanBuffer}
              setTransferScanBuffer={setTransferScanBuffer}
              transferScanError={transferScanError}
              isLookingUpTransfer={isLookingUpTransfer}
              handleTransferKeyDown={handleTransferKeyDown}
              handleSelectTransfer={handleSelectTransfer}
              transferInputRef={transferInputRef}
              onResetBadge={() => {
                setSupervisorId('');
                setSupervisorName('');
                setStep('badge');
                clearScanInput();
              }}
            />
          )}
          {step === 'scanning' && (
            <TransferScanningStep
              selectedTransfer={selectedTransfer}
              setStep={setStep}
              setSelectedTransfer={setSelectedTransfer}
              scannedBoxes={scannedBoxes}
              setScannedBoxes={setScannedBoxes}
              decisionMode={decisionMode}
              setDecisionMode={setDecisionMode}
              pendingBox={pendingBox}
              scanError={scanError}
              handleKeyDown={handleKeyDown}
              processBoxScan={processBoxScan}
              handleBoxDecision={handleBoxDecision}
              removeScannedBox={removeScannedBox}
              showRejectReasons={showRejectReasons}
              setShowRejectReasons={setShowRejectReasons}
              showHoldReasons={showHoldReasons}
              setShowHoldReasons={setShowHoldReasons}
              showManualAdd={showManualAdd}
              setShowManualAdd={setShowManualAdd}
              manualSelectedItem={manualSelectedItem}
              setManualSelectedItem={setManualSelectedItem}
              manualWeight={manualWeight}
              setManualWeight={setManualWeight}
              manualWeightRef={manualWeightRef}
              handleManualAddConfirm={handleManualAddConfirm}
              showScanSummary={showScanSummary}
              setShowScanSummary={setShowScanSummary}
              handleProceedToDecision={handleProceedToDecision}
              inputRef={inputRef}
              isPreSelected={!!initialTransferNumber}
              onBackToList={() => navigate('/supervisor/transfers')}
            />
          )}
          {step === 'decision' && (
            <TransferDecisionStep
              selectedTransfer={selectedTransfer}
              setStep={setStep}
              scannedBoxes={scannedBoxes}
              decisionMode={decisionMode}
              itemDecisions={itemDecisions}
              submitting={submitting}
              showConfirmDialog={showConfirmDialog}
              setShowConfirmDialog={setShowConfirmDialog}
              handleSubmit={handleSubmit}
            />
          )}
        </>
      )}
      {tabMode === 'history' && (
        <TransferHistoryTab
          historyTransfers={historyTransfers}
          loadingHistory={loadingHistory}
          historyPage={historyPage}
          setHistoryPage={setHistoryPage}
          historyTotalPages={historyTotalPages}
          expandedHistoryTransfer={expandedHistoryTransfer}
          setExpandedHistoryTransfer={setExpandedHistoryTransfer}
          expandedHistoryBoxes={expandedHistoryBoxes}
          setExpandedHistoryBoxes={setExpandedHistoryBoxes}
        />
      )}
    </>
  );

  // Mobile layout with primary header
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
        {/* Mobile Header */}
        <header className="bg-primary-600 dark:bg-primary-700 text-white px-4 py-3 sticky top-0 z-10 flex items-center gap-3">
          <button
            onClick={() => navigate('/supervisor')}
            className="p-2 -ml-2 rounded-lg hover:bg-white/10 active:bg-white/20 transition-colors"
          >
            <LayoutGrid className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold truncate">{t('transferVerification.title')}</h1>
          </div>
          {/* Avatar dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="p-1 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 z-50">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.firstName && user?.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user?.username
                    }
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {user?.role}
                  </div>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { toggleTheme(); setUserMenuOpen(false); }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {theme === 'dark' ? <Sun className="h-4 w-4 mr-3" /> : <Moon className="h-4 w-4 mr-3" />}
                    {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                  </button>
                  <button
                    onClick={() => {
                      const next = language === 'en' ? 'zh-TW' : language === 'zh-TW' ? 'es' : 'en';
                      setLanguage(next as any);
                      setUserMenuOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Globe className="h-4 w-4 mr-3" />
                    {language === 'en' ? 'English' : language === 'zh-TW' ? '繁體中文' : 'Español'}
                  </button>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-md"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {mainContent}
        </main>

        {/* Sticky footer: View All Transfers */}
        <div className="sticky bottom-0 px-3 py-2 bg-gray-100/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => navigate('/supervisor/transfers')}
            className="w-full py-2.5 flex items-center justify-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98] transition-all"
          >
            <ListChecks className="w-4 h-4" />
            {t('transferVerification.viewAllTransfers') || 'View All Transfers'}
          </button>
        </div>

      </div>
    );
  }

  // Desktop layout
  return (
    <div className={embedded ? 'relative h-full flex flex-col overflow-hidden' : 'relative max-w-2xl mx-auto'}>
      {/* Header — hidden when embedded (parent provides its own header) */}
      {!embedded && (
        <div className="hidden md:flex p-4 border-b border-gray-200 dark:border-gray-700 items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('transferVerification.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('transferVerification.subtitle')}
            </p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={embedded ? 'flex-1 overflow-y-auto' : ''}>
        {mainContent}
      </div>

    </div>
  );
};

export default TransferVerification;
